import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../utils/api';

export default function ResetPassword() {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setError(t('auth.passwordsDoNotMatch', 'Passwords do not match.'));
            return;
        }
        setError('');
        setLoading(true);
        try {
            await api.request('/auth/reset-password', {
                method: 'POST',
                body: JSON.stringify({ token, new_password: password })
            });
            setSuccess(true);
            setTimeout(() => navigate('/login'), 3000);
        } catch (err) {
            setError(err.message || t('auth.resetFailed', 'Failed to reset password.'));
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-base)' }}>
                <p style={{ color: 'var(--text-muted)' }}>{t('auth.invalidToken', 'Invalid or missing reset token.')}</p>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', background: 'var(--bg-base)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem 1.5rem', fontFamily: "'Roboto', sans-serif", position: 'relative', overflow: 'hidden' }}>

            {/* Background glow */}
            <div style={{ position: 'absolute', top: '30%', left: '50%', transform: 'translate(-50%, -50%)', width: '600px', height: '600px', borderRadius: '50%', background: 'radial-gradient(circle, var(--orange-glow) 0%, transparent 70%)', pointerEvents: 'none' }} />

            <div style={{ width: '100%', maxWidth: '440px', position: 'relative', zIndex: 1 }}>

                <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
                    <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '2.5rem', letterSpacing: '0.08em', color: '#ffffff', marginBottom: '0.4rem' }}>
                        {t('auth.resetPassword', 'Reset Password')}
                    </h1>
                    <div style={{ width: '40px', height: '3px', background: 'var(--orange)', borderRadius: '2px', margin: '0 auto 0.75rem' }} />
                    <p style={{ fontSize: '0.88rem', color: 'var(--text-muted)' }}>
                        {t('auth.resetPasswordSubtitle', 'Enter a new password for your account.')}
                    </p>
                </div>

                <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '2.5rem', position: 'relative', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, var(--orange-dark), var(--orange), var(--orange-light))' }} />

                    {error && (
                        <div style={{ marginBottom: '1.25rem', padding: '0.85rem 1rem', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', color: '#fca5a5', fontSize: '0.85rem' }}>
                            {error}
                        </div>
                    )}
                    {success ? (
                        <div style={{ marginBottom: '1.25rem', padding: '0.85rem 1rem', background: 'rgba(34, 197, 94, 0.08)', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: '6px', color: '#86efac', fontSize: '0.85rem', textAlign: 'center' }}>
                            {t('auth.passwordResetSuccess', 'Password has been reset successfully. Redirecting to login...')}
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <label style={{ display: 'block', fontFamily: "'Oswald', sans-serif", fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                                {t('auth.newPassword', 'New Password')}
                            </label>
                            <input
                                id="password"
                                type="password"
                                placeholder={t('auth.placeholderPassword', '********')}
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                required
                                className="barber-input"
                                style={{ marginBottom: '1.25rem' }}
                            />

                            <label style={{ display: 'block', fontFamily: "'Oswald', sans-serif", fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                                {t('auth.confirmPassword', 'Confirm Password')}
                            </label>
                            <input
                                id="confirmPassword"
                                type="password"
                                placeholder={t('auth.placeholderPassword', '********')}
                                value={confirmPassword}
                                onChange={e => setConfirmPassword(e.target.value)}
                                required
                                className="barber-input"
                                style={{ marginBottom: '2rem' }}
                            />

                            <button
                                type="submit"
                                disabled={loading}
                                className="btn-orange"
                                style={{ width: '100%', justifyContent: 'center', fontSize: '0.95rem', padding: '0.9rem', opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                            >
                                {loading ? t('common.loading', 'Loading...') : t('auth.resetPassword', 'Reset Password')}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
